import { useToast } from "@ligo/layout-design-core";
import { Errors } from "classes";
import { popupSize, projectKey, softphoneKey, ss } from "consts";
import SelectProject from "modals/selectProject";
import {
  BaseProviderType,
  CodeSoftphoneEnum,
  externalPathEnum,
  MyUserModel,
  ProjectOptModel,
} from "models";
import { useAuth } from "providers";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as ListService from "services/list";
import * as UserService from "services/user";
import {
  aytyFormatError,
  objectToURLParams,
  sortBy,
  windowOpenSessionCleaner,
} from "utils";

type DefaultSizesType = {
  ddd?: number;
  phone?: number;
};

type ProfileContextType = {
  user?: MyUserModel;
  isSagoCore: boolean;
  isSagoDS: boolean;
  isDevelopmentRole: boolean;
  projectOpts?: ProjectOptModel[];
  customerProjectOpts?: ProjectOptModel[];
  pIdProject?: number;
  pIdSoftphone?: CodeSoftphoneEnum;
  selectedProject?: ProjectOptModel;
  defaultSizes: DefaultSizesType;
  getProjectOpts: () => Promise<void>;
  goOut: () => void;
  onOpenProjectSelect: () => void;
  onChangeProject: (newId: number, code?: CodeSoftphoneEnum) => void;
  onOpenNotepad: () => void;
};

const coreCodeProject = 3;
const developmentRole = 7;

let notepadWindow: Window | null;

const ProfileContext = createContext<ProfileContextType>(
  {} as ProfileContextType,
);

export const ProfileProvider = ({ children }: BaseProviderType) => {
  const [modal, setModal] = useState(false);
  const [user, setUser] = useState<MyUserModel>();
  const [projectOpts, setProjectOpts] = useState<ProjectOptModel[]>();
  const [pIdProject, setPIdProject] = useState<number>();
  const [pIdSoftphone, setPIdSoftphone] = useState<CodeSoftphoneEnum>();
  const { token, signOut } = useAuth();
  const { error, warning, clearToasts } = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const errorsResolver = useMemo(
    () => new Errors({ error, warning }, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const isSagoCore = useMemo(
    () => pIdProject === coreCodeProject,
    [pIdProject],
  );

  const isSagoDS = useMemo(() => !!user && user.isDialerProxy, [user]);

  const isDevelopmentRole = useMemo(
    () => user?.idRole === developmentRole,
    [user],
  );

  const defaultSizes = useMemo(
    () => ({
      ddd: user?.qtDigitsDDD,
      phone: user?.qtDigitsPhone,
    }),
    [user],
  );

  const selectedProject = useMemo(
    () => projectOpts?.find((p) => p.IdProject === pIdProject),
    [projectOpts, pIdProject],
  );

  const customerProjectOpts = useMemo(
    () => projectOpts?.filter((p) => p.IdProject !== coreCodeProject),
    [projectOpts],
  );

  const onOpenProjectSelect = useCallback(() => setModal(true), []);

  useEffect(() => {
    const currentId = ss.getItem(projectKey);
    if (projectOpts?.length)
      switch (true) {
        case !!currentId:
          setPIdProject(Number(currentId));
          break;
        case projectOpts.length === 1 && !projectOpts[0].MustSelectSoftphone:
          setPIdProject(projectOpts[0].IdProject);
          break;
        default:
          onOpenProjectSelect();
      }

    const currentSoftphoneId = ss.getItem(softphoneKey);
    currentSoftphoneId && setPIdSoftphone(Number(currentSoftphoneId));
  }, [projectOpts, onOpenProjectSelect]);

  useEffect(() => {
    if (user) {
      const { hasAccessAttend, mustLoginPaused } = user;

      const hasAccess = hasAccessAttend && !mustLoginPaused;
      const projectIsReleased = pIdProject && !isSagoCore && !isSagoDS;
      const inHomePage = window.location.pathname === "/";

      if (hasAccess && projectIsReleased && inHomePage) navigate("/attendance");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pIdProject, user, isSagoCore, isSagoDS]);

  const getProjectOpts = useCallback(async () => {
    if (token)
      await ListService.getProjectOpts({})
        .then(({ data }) => {
          if (data.idReturnAPI > 0) {
            let coreProjects: ProjectOptModel[] = [];
            let current: ProjectOptModel[] = [];

            data.projectList?.forEach((p) => {
              if (p.IsActive)
                switch (true) {
                  case p.IdProject === coreCodeProject:
                    coreProjects.push(p);
                    break;
                  default:
                    current.push(p);
                    break;
                }
            });

            current = sortBy(current, "DeProject");

            setProjectOpts([...coreProjects, ...current]);
          } else warning({ description: t(aytyFormatError(data)) });
        })
        .catch(errorsResolver.defaultError);
  }, [token, errorsResolver, warning, t]);

  useEffect(() => {
    getProjectOpts();
  }, [getProjectOpts]);

  const getUser = useCallback(async () => {
    if (pIdProject)
      await UserService.getMe({ pIdProject, pIdSoftphone })
        .then(({ data }) => {
          const { dtServer, deReturnAPI, idReturnAPI, ...args } = data;
          if (idReturnAPI > 0) setUser(args);
          else warning({ description: t(aytyFormatError(data)) });
        })
        .catch(errorsResolver.defaultError);
  }, [pIdProject, pIdSoftphone, errorsResolver, warning, t]);

  useEffect(() => {
    getUser();
  }, [getUser]);

  const clearUser = useCallback(() => {
    notepadWindow?.close();
    ss.removeItem(projectKey);
    ss.removeItem(softphoneKey);
    setProjectOpts(undefined);
    setPIdSoftphone(undefined);
    setPIdProject(undefined);
    setUser(undefined);
  }, []);

  const goOut = useCallback(async () => {
    await UserService.signOut()
      .then(({ data }) => {
        if (data.idReturnAPI > 0) {
          clearToasts();
          clearUser();
          signOut();
        } else warning({ description: t(aytyFormatError(data)) });
      })
      .catch(errorsResolver.defaultError);
  }, [errorsResolver, clearToasts, clearUser, signOut, warning, t]);

  const onChangeProject = useCallback(
    (newId: number, code?: CodeSoftphoneEnum) => {
      if (newId !== pIdProject) {
        setUser(undefined);
        setPIdProject(newId);
        ss.setItem(projectKey, newId.toString());
      }

      if (code !== pIdSoftphone) {
        setPIdSoftphone(code);
        ss.setItem(softphoneKey, code?.toString() ?? "");
      }
    },
    [pIdProject, pIdSoftphone],
  );

  const onOpenNotepad = useCallback(() => {
    if (notepadWindow?.closed === false) notepadWindow.focus();
    else {
      const { x, y } = popupSize;
      const left = window.screen.width / 2 - x / 2;
      const top = window.screen.height / 2 - y / 2;

      notepadWindow = windowOpenSessionCleaner(
        `${externalPathEnum.notepad}?${objectToURLParams({ token })}`,
        "Notepad",
        `$width=${x},height=${y},left=${left},top=${top}`,
      );
    }

    return () => notepadWindow?.close();
  }, [token]);

  return (
    <ProfileContext.Provider
      value={{
        user,
        isSagoCore,
        isSagoDS,
        isDevelopmentRole,
        projectOpts,
        customerProjectOpts,
        pIdProject,
        pIdSoftphone,
        selectedProject,
        defaultSizes,
        getProjectOpts,
        goOut,
        onOpenProjectSelect,
        onChangeProject,
        onOpenNotepad,
      }}
    >
      <SelectProject open={modal} onClose={() => setModal(false)} />
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => useContext(ProfileContext);
